import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatError } from '@/lib/api';
import { Scales, Eye, EyeSlash } from '@phosphor-icons/react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1733951433050-e29718fd08f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3R1cmFsJTIwZ2VvbWV0cmljJTIwbGluZXMlMjBiYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fHwxNzc2MDQ1MTY3fDA&ixlib=rb-4.1.0&q=85"
          alt="ClarityCase AI" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#002FA7]/80" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-outfit text-4xl font-bold mb-3">ClarityCase AI</h2>
          <p className="text-lg text-white/80 font-ibm max-w-md">AI-powered investigation platform. Upload documents, build timelines, detect contradictions.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F9FAFB]">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <Scales weight="duotone" className="w-8 h-8 text-[#002FA7]" />
            <span className="font-outfit text-2xl font-bold text-[#09090B]">ClarityCase AI</span>
          </div>
          <h1 className="font-outfit text-3xl font-bold text-[#09090B] mb-1">Sign in</h1>
          <p className="text-[#6B7280] font-ibm mb-8">Enter your credentials to access your cases</p>
          {error && <div data-testid="login-error" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm font-ibm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2 font-ibm">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md bg-white text-[#09090B] font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent transition-all" placeholder="you@example.com" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2 font-ibm">Password</label>
              <div className="relative">
                <input data-testid="login-password-input" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md bg-white text-[#09090B] font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent transition-all pr-12" placeholder="Enter your password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#09090B]">
                  {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full py-3 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] active:scale-[0.98] transition-all disabled:opacity-50">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-[#6B7280] font-ibm">
            Don't have an account? <Link to="/register" className="text-[#002FA7] font-semibold hover:underline" data-testid="register-link">Create one</Link>
          </p>
          <p className="mt-2 text-center text-sm text-[#6B7280] font-ibm">
            <Link to="/forgot-password" className="text-[#002FA7] font-semibold hover:underline" data-testid="forgot-password-link">Forgot your password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
